import { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import LiveMapGL from './LiveMapGL';

// Using Leaflet for free maps (no API key needed) — kept as a feature-flag
// fallback so we can revert instantly if the Mapbox GL v3 path has an issue
// in the wild. Set REACT_APP_USE_MAPBOX_GL=false (or unset the Mapbox token)
// to route through Leaflet again.
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const USE_MAPBOX_GL = (process.env.REACT_APP_USE_MAPBOX_GL || '').toLowerCase() === 'true'
  && !!process.env.REACT_APP_MAPBOX_TOKEN;

export default function LiveMap(props) {
  // Feature flag: use the modern Mapbox GL v3 renderer when the token is
  // present + the flag is on. Falls through to the old Leaflet path below
  // when either condition isn't met.
  if (USE_MAPBOX_GL) {
    return <LiveMapGL {...props} />;
  }
  return <LiveMapLeaflet {...props} />;
}

function LiveMapLeaflet({ 
  driverLocation, 
  pickupLocation, 
  dropoffLocation,
  customerLocation,
  showRoute = false,
  height = '300px'
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const [mapReady, setMapReady] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load Leaflet
  useEffect(() => {
    if (window.L) {
      setMapReady(true);
      setLoading(false);
      return;
    }

    // Load CSS — but only if the static preload in index.html didn't
    // already inject it. Double-loading the same stylesheet has caused
    // subtle race conditions with tile-grid measurement.
    let link = null;
    const already = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .some((l) => l.href === LEAFLET_CSS);
    if (!already) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    // Load JS
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.onload = () => {
      setMapReady(true);
      setLoading(false);
    };
    document.head.appendChild(script);

    return () => {
      if (link && link.parentNode) link.parentNode.removeChild(link);
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;

    const L = window.L;
    
    // Default center (Austin, TX)
    const defaultCenter = [30.2672, -97.7431];
    const center = driverLocation 
      ? [driverLocation.lat, driverLocation.lng]
      : pickupLocation 
        ? [pickupLocation.lat, pickupLocation.lng]
        : defaultCenter;

    mapInstanceRef.current = L.map(mapRef.current).setView(center, 14);

    // Add tile layer (OpenStreetMap - free)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(mapInstanceRef.current);

    // Leaflet computes its render size at init from the container's clientHeight.
    // On PWA loads, a container that transitions from spinner→map often has
    // its correct height AFTER Leaflet's first paint, resulting in the map
    // rendering as a black rectangle with no tiles. Two-step fix:
    //   1. Invalidate size after the layout has settled (rAF + delay).
    //   2. Re-invalidate every time the tab becomes visible / window resizes,
    //      so PWA foregrounding or split-screen resize never leaves a stale
    //      viewport.
    //
    // iOS Safari extra: half the map renders black when the dynamic viewport
    // toolbar collapses/expands (address bar hide-on-scroll). The safari
    // agent-string check widens the invalidate schedule + adds an
    // orientationchange listener + a tiny pan nudge to force tile refetch.
    const forceResize = () => {
      try {
        const m = mapInstanceRef.current;
        if (!m) return;
        m.invalidateSize(true);
        // Nudge pan by 1px then back — forces Leaflet to re-request any tiles
        // that were skipped during the initial black-render frame on iOS.
        const c = m.getCenter();
        m.panBy([1, 0], { animate: false });
        m.panTo(c, { animate: false });
      } catch { /* ignore */ }
    };
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isIOSSafari = /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    // Multiple invalidations at different tick offsets to cover:
    //   - the immediate frame  (container just committed)
    //   - the next paint      (fonts/CSS loaded)
    //   - the slow tablet case (~800ms after network CSS)
    //   - the iOS Safari toolbar-collapse case (up to ~3s after first paint)
    requestAnimationFrame(forceResize);
    setTimeout(forceResize, 200);
    setTimeout(forceResize, 800);
    if (isIOSSafari) {
      setTimeout(forceResize, 1600);
      setTimeout(forceResize, 3000);
    }

    // ResizeObserver — the definitive fix for the "map only renders on the
    // left half" bug. Some layout paths (header expands after booking loads,
    // driver card mounts, PWA safe-area insets settle, iOS split-view
    // resize) let the container's clientWidth grow AFTER Leaflet's initial
    // tile grid measurement. The timed forceResize callbacks miss that, so
    // Leaflet keeps painting tiles for the smaller viewport, leaving black
    // gutters on the right. Observing the container guarantees a resize
    // whenever the actual box changes size.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && mapRef.current) {
      ro = new ResizeObserver(() => forceResize());
      ro.observe(mapRef.current);
    }

    const onResize = () => forceResize();
    const onVis = () => { if (document.visibilityState === 'visible') forceResize(); };
    const onOrient = () => { setTimeout(forceResize, 250); };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrient);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrient);
      document.removeEventListener('visibilitychange', onVis);
      if (ro) ro.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [mapReady]);

  // Update markers
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;

    const L = window.L;
    const map = mapInstanceRef.current;

    // Driver marker (animated speeding car icon that rotates to bearing)
    if (driverLocation) {
      // Compute bearing (0-360) from previous position → new position so
      // the car icon always points where it's going.
      const prev = markersRef.current.__driverLastPos;
      let bearing = markersRef.current.__driverBearing || 0;
      if (prev) {
        const lat1 = (prev.lat * Math.PI) / 180;
        const lat2 = (driverLocation.lat * Math.PI) / 180;
        const dLng = ((driverLocation.lng - prev.lng) * Math.PI) / 180;
        const y = Math.sin(dLng) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) -
                  Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
        const distSquared = Math.pow(driverLocation.lat - prev.lat, 2) +
                            Math.pow(driverLocation.lng - prev.lng, 2);
        // Only update bearing if the driver actually moved a meaningful amount
        // (avoids the car spinning wildly at a red light on GPS jitter).
        if (distSquared > 1e-9) {
          bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
        }
      }
      markersRef.current.__driverBearing = bearing;
      markersRef.current.__driverLastPos = { lat: driverLocation.lat, lng: driverLocation.lng };

      // Speeding-car SVG in a rotating, pulsing frame. The outer ring
      // pulses to signal "live", the inner car rotates to the direction
      // of travel, and a soft trail behind it hints at motion.
      const svgHtml = `
        <div class="link-driver-marker" style="position:relative;width:56px;height:56px;transform:rotate(${bearing}deg);transition:transform 400ms cubic-bezier(.25,.8,.25,1)">
          <div style="position:absolute;inset:6px;border-radius:50%;background:radial-gradient(circle,rgba(0,71,255,0.35) 0%,rgba(0,71,255,0) 70%);animation:linkDriverPulse 1.6s ease-out infinite"></div>
          <div style="position:absolute;left:50%;top:50%;width:40px;height:40px;margin:-20px 0 0 -20px;border-radius:50%;background:linear-gradient(135deg,#0047FF,#003BE0);border:2px solid rgba(255,255,255,0.9);box-shadow:0 4px 16px rgba(0,71,255,0.55),0 0 0 3px rgba(0,71,255,0.15);display:flex;align-items:center;justify-content:center;">
            <!-- Top-down car silhouette. Nose points UP; container rotates
                 the whole thing to bearing so the car always faces travel. -->
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
              <!-- Body -->
              <path d="M9 9c0-1.7 1.3-3 3-3h8c1.7 0 3 1.3 3 3v14c0 1.7-1.3 3-3 3h-8c-1.7 0-3-1.3-3-3V9z"
                    fill="white" stroke="rgba(0,0,0,0.25)" stroke-width="0.5"/>
              <!-- Front windshield (wider at nose) -->
              <path d="M11 8.5c0-.6.4-1 1-1h8c.6 0 1 .4 1 1v3.5H11V8.5z"
                    fill="#0B1F5C" opacity="0.85"/>
              <!-- Rear windshield -->
              <path d="M11 20h10v3.5c0 .6-.4 1-1 1h-8c-.6 0-1-.4-1-1V20z"
                    fill="#0B1F5C" opacity="0.85"/>
              <!-- Headlights -->
              <circle cx="12" cy="7" r="0.9" fill="#FFF3AA"/>
              <circle cx="20" cy="7" r="0.9" fill="#FFF3AA"/>
              <!-- Tail lights -->
              <rect x="11" y="24.3" width="2.4" height="1" rx="0.4" fill="#FF4747"/>
              <rect x="18.6" y="24.3" width="2.4" height="1" rx="0.4" fill="#FF4747"/>
              <!-- Roof line accent -->
              <line x1="12.5" y1="14" x2="19.5" y2="14" stroke="rgba(255,255,255,0.5)" stroke-width="0.6"/>
              <line x1="12.5" y1="17" x2="19.5" y2="17" stroke="rgba(255,255,255,0.5)" stroke-width="0.6"/>
            </svg>
          </div>
        </div>
        <style>
          @keyframes linkDriverPulse {
            0% { transform: scale(.6); opacity: .9; }
            80% { transform: scale(1.6); opacity: 0; }
            100% { transform: scale(1.6); opacity: 0; }
          }
        </style>
      `;

      if (markersRef.current.driver) {
        // Smoothly animate from current position to the new one instead of
        // jumping. Leaflet doesn't have a built-in tween, so lerp manually.
        const marker = markersRef.current.driver;
        const start = marker.getLatLng();
        const end = { lat: driverLocation.lat, lng: driverLocation.lng };
        const dur = 700; // ms
        const t0 = performance.now();
        // Cancel any prior animation frame
        if (markersRef.current.__animFrame) cancelAnimationFrame(markersRef.current.__animFrame);
        const step = (now) => {
          const p = Math.min(1, (now - t0) / dur);
          const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
          const lat = start.lat + (end.lat - start.lat) * eased;
          const lng = start.lng + (end.lng - start.lng) * eased;
          marker.setLatLng([lat, lng]);
          if (p < 1) {
            markersRef.current.__animFrame = requestAnimationFrame(step);
          }
        };
        markersRef.current.__animFrame = requestAnimationFrame(step);
        // Refresh the rotating icon HTML with the new bearing
        marker.setIcon(L.divIcon({
          className: 'driver-marker',
          html: svgHtml,
          iconSize: [56, 56],
          iconAnchor: [28, 28],
        }));
      } else {
        const driverIcon = L.divIcon({
          className: 'driver-marker',
          html: svgHtml,
          iconSize: [56, 56],
          iconAnchor: [28, 28],
        });
        markersRef.current.driver = L.marker([driverLocation.lat, driverLocation.lng], { icon: driverIcon }).addTo(map);
      }

      // Pan to driver
      map.panTo([driverLocation.lat, driverLocation.lng]);
    }

    // Pickup marker (green)
    if (pickupLocation && !markersRef.current.pickup) {
      const pickupIcon = L.divIcon({
        className: 'pickup-marker',
        html: `<div style="background: #22c55e; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="12" r="4"/></svg>
        </div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      markersRef.current.pickup = L.marker([pickupLocation.lat, pickupLocation.lng], { icon: pickupIcon })
        .addTo(map)
        .bindPopup('Pickup');
    }

    // Dropoff marker (red)
    if (dropoffLocation && !markersRef.current.dropoff) {
      const dropoffIcon = L.divIcon({
        className: 'dropoff-marker',
        html: `<div style="background: #ef4444; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
        </div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30]
      });
      markersRef.current.dropoff = L.marker([dropoffLocation.lat, dropoffLocation.lng], { icon: dropoffIcon })
        .addTo(map)
        .bindPopup('Dropoff');
    }

    // Fit bounds if multiple markers
    const bounds = [];
    if (driverLocation) bounds.push([driverLocation.lat, driverLocation.lng]);
    if (pickupLocation) bounds.push([pickupLocation.lat, pickupLocation.lng]);
    if (dropoffLocation) bounds.push([dropoffLocation.lat, dropoffLocation.lng]);
    
    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [driverLocation, pickupLocation, dropoffLocation]);

  // Render the map container ALWAYS (even during Leaflet-JS load) so it has
  // its computed size ready when Leaflet initializes. The loading spinner
  // sits on top of it as an overlay. This is what fixes the "black map"
  // bug on PWA first-loads where the container was still hidden inside the
  // spinner branch when Leaflet measured it.
  return (
    <div
      style={{ height, width: '100%', position: 'relative' }}
      className="rounded-xl overflow-hidden bg-zinc-900"
    >
      <div
        ref={mapRef}
        style={{ height: '100%', width: '100%' }}
        data-testid="live-map"
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-800/80 backdrop-blur-sm">
          <Loader2 className="w-8 h-8 text-[#4D88FF] animate-spin" />
        </div>
      )}
    </div>
  );
}
